import { useCallback, useContext, useMemo, useState, useEffect } from "react"
import { isEmpty, isEqual, size, noop } from "lodash"
import PropTypes from "prop-types"
import Filters from "components/analyze-header-filters"
import { VIEW_TYPES } from "helpers/base"
import { combineTanStackStatuses } from "helpers/data-statuses"
import { useFilterSecuritiesData, useClassificationsData, computeUpdatedFilters, computeAddedFilters } from "helpers/filters"
import { computeFilterOns } from "helpers/filters/filter-ons"
import { NORMALIZATION } from "helpers/segments"
import { useAccountAlias } from "resources/account"
import { useAssetFilters, useSecuritiesDescriptors } from "resources/filters"
import { useTagCategories } from "resources/tags"
import { useAttributeCategories } from "resources/attributes"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "../../analytics"

export default function FilterSelector({ context, filtersDrawerOpen = false, setFilterSelectorStatus = noop, hasMultipleInstruments = false, filterButtonRef, toggleFilters }) {
  const { modelId, instrument, secondInstrument, comparisonInstrument, endDate, base, filter, setFilter, trades, normalization, setNormalization, isExpanded, isIntraday } = useContext(context)
  const analytics = useAnalytics()

  const [editingFilter, setEditingFilter] = useState(null)
  const [editingFilterInstrument, setEditingFilterInstrument] = useState(null)

  const account = useAccountAlias()
  const isActive = !!base
  const [assetFilters, assetFiltersStatus] = useAssetFilters(modelId, account, instrument, endDate, endDate, isIntraday)

  // This creates a TICKER.MIC id from each security in the filter
  const securitiesIds = useMemo(() => Object.values(filter?.securities || {}).reduce((acc, filterTypeSecurities) =>
    [...acc, ...filterTypeSecurities.map(({ ticker, mic, country }) => `${ticker}.${mic || country}`)], []),
  [filter])

  const [filterSecurities, filterSecuritiesStatus] = useFilterSecuritiesData(
    modelId,
    account,
    instrument,
    secondInstrument,
    comparisonInstrument,
    endDate,
    isExpanded,
  )

  const [tagCategories, tagCategoriesStatus] = useTagCategories(instrument, account)
  const [attributeCategories, attributeCategoriesStatus] = useAttributeCategories(instrument, account)
  const [securitiesDescriptors, securitiesDescriptorsStatus] = useSecuritiesDescriptors(account, modelId, securitiesIds, endDate)

  const {
    classifications,
    classificationsValues,
    flattenedClassificationsValues,
    status: classificationsStatus,
  } = useClassificationsData(endDate, filter)

  const filterOns = useMemo(() => {
    if (isEmpty(assetFilters)) return []

    const viewType = isActive ? VIEW_TYPES.ACTIVE : VIEW_TYPES.ABSOLUTE
    return computeFilterOns(
      viewType,
      assetFilters,
      filter,
      editingFilter,
      tagCategories,
      classifications?.map(({ versions, ...classification }) => ({ ...classification, version: versions.at(-1) })) || [],
      classificationsValues,
      attributeCategories,
      instrument?.type,
      normalization,
      hasMultipleInstruments,
    )
  }, [isActive, assetFilters, filter, editingFilter, tagCategories, classifications, classificationsValues, attributeCategories, instrument?.type, normalization, hasMultipleInstruments])

  const handleUpdateFilters = useCallback((filters) => {
    const { filter } = computeUpdatedFilters(filters)

    // Track filter changes
    if (filter && Object.keys(filter).length > 0) {
      analytics.track(DASHBOARD_EVENTS.FILTER_APPLIED, { filter })
    } else if (isEmpty(filter)) {
      analytics.track(DASHBOARD_EVENTS.FILTER_CLEARED)
    }

    setFilter(filter)
  }, [setFilter, analytics])

  // If switching between instruments, we want to clear the filter
  useEffect(() => {
    if (!isEmpty(filter) && !isEqual(editingFilterInstrument, instrument)) {
      setEditingFilter(null)
      setFilter(null)
      setEditingFilterInstrument(instrument)
    } else if (isEmpty(filter) && !editingFilterInstrument) {
      setEditingFilterInstrument(instrument)
    }
  }, [instrument, editingFilterInstrument, filter, setFilter, setEditingFilter, setEditingFilterInstrument])

  const status = combineTanStackStatuses([
    classificationsStatus,
    filterSecuritiesStatus,
    tagCategoriesStatus,
    attributeCategoriesStatus,
    assetFiltersStatus,
    securitiesDescriptorsStatus,
  ])

  // This component's visual handle is actually the Filter button inside the Dashboard component. We expose the status to the
  // Dashboard component so it can disable the button while it's loading or if there's an error.
  useEffect(() => {
    setFilterSelectorStatus(status)
  }, [status, setFilterSelectorStatus])

  const addedFilters = useMemo(() => {
    return computeAddedFilters(filter, filterOns, {
      tagCategories,
      securitiesDescriptors,
      attributeCategories,
      classificationsInfos: {
        classificationsMetas: classifications,
        classificationsValues: flattenedClassificationsValues,
      },
      portfolioFilters: assetFilters,
    })
  }, [
    filter,
    filterOns,
    tagCategories,
    classifications,
    securitiesDescriptors,
    assetFilters,
    attributeCategories,
    flattenedClassificationsValues,
  ])

  const hasTrades = size(trades) > 0
  // [TODO] startDate, intraday, isExpanded, currency, hasUnsupportedContributionFilter
  return filtersDrawerOpen && (
    <Filters
      account={account}
      assetId={instrument?.id}
      assetType={instrument?.type}
      endDate={endDate}
      filterOns={filterOns}
      heapAddFilterId="dashboard"
      modelId={modelId}
      securityIds={filterSecurities}
      normalization={hasTrades ? NORMALIZATION.CONTRIBUTION : normalization}
      setNormalization={setNormalization}
      disableNormalization={hasTrades}
      disableNormalizationTooltip={hasTrades ? "Standalone normalization is not supported for trades" : null}
      addedFilters={addedFilters}
      updateFilters={handleUpdateFilters}
      anchorElement={filterButtonRef}
      filtersVisible={filtersDrawerOpen}
      toggleFilters={toggleFilters}
    />
  )
}

FilterSelector.propTypes = {
  context: PropTypes.object.isRequired,
  filtersDrawerOpen: PropTypes.bool,
  setFilterSelectorStatus: PropTypes.func,
  hasMultipleInstruments: PropTypes.bool,
  filterButtonRef: PropTypes.object,
  toggleFilters: PropTypes.func,
}
